import React, { useMemo } from 'react';
import * as yup from 'yup';
import { SchemaOf } from 'yup';
import {
    RhCheckbox,
    RhDateInput,
    RhFormWrapper,
    RHTextField,
} from '../../../../../dum';
import { FIELD_LABELS, shortLabel } from '../../../../../../helpers';
import {
    clearManagement,
    useUpdateExpenseDetailsMutation,
} from '../../../../../../services';
import { FormFieldSection } from '../../form-field-section';

interface IExpenseDetailsChange {
    notes?: string;
    expected_payment_date?: string | null;
    require_external_code: boolean;
    external_code?: string | null;
    require_supplement: boolean;
    supplement_code?: string | null;
    reconciliation_only: boolean;
    is_draft: boolean;
    canceled: boolean;
}

interface IExpenseDetailsChangeFormProps {
    expenseId: number;
    currentNotes?: string | null;
    currentExpectedPaymentDate?: string | null;
    currentRequireExternalCode?: boolean | null;
    currentExternalCode?: string | null;
    currentRequireSupplement?: boolean | null;
    currentSupplementCode?: string | null;
    currentReconciliationOnly?: boolean | null;
    currentIsDraft?: boolean | null;
    currentCanceled?: boolean | null;
    onSuccess: () => void;
}

// Lets expense users amend the optional, side-effect-free fields of an expense
// (expected payment date, folio, supplement, conciliation, draft, canceled)
// without going through the full expense upsert and its status-1 edit lock. The
// financial fields (subtotal / tax / resources) are intentionally excluded —
// they would force a totals recompute, which the lightweight
// updateExpenseDetails endpoint avoids. The expense counterpart of
// OrderSaleDetailsChangeForm.
export function ExpenseDetailsChangeForm({
    expenseId,
    currentNotes,
    currentExpectedPaymentDate,
    currentRequireExternalCode,
    currentExternalCode,
    currentRequireSupplement,
    currentSupplementCode,
    currentReconciliationOnly,
    currentIsDraft,
    currentCanceled,
    onSuccess,
}: IExpenseDetailsChangeFormProps) {
    const [mutation, { loading }] = useUpdateExpenseDetailsMutation({
        update: (cache) => {
            clearManagement(cache);
        },
    });

    const initialValues = useMemo<IExpenseDetailsChange>(() => {
        return {
            notes: currentNotes || '',
            expected_payment_date: currentExpectedPaymentDate || null,
            require_external_code: currentRequireExternalCode || false,
            external_code: currentExternalCode || '',
            require_supplement: currentRequireSupplement || false,
            supplement_code: currentSupplementCode || '',
            reconciliation_only: currentReconciliationOnly || false,
            is_draft: currentIsDraft || false,
            canceled: currentCanceled || false,
        };
    }, [
        currentNotes,
        currentExpectedPaymentDate,
        currentRequireExternalCode,
        currentExternalCode,
        currentRequireSupplement,
        currentSupplementCode,
        currentReconciliationOnly,
        currentIsDraft,
        currentCanceled,
    ]);

    const validationSchema: SchemaOf<IExpenseDetailsChange> = yup.object({
        notes: yup.string(),
        expected_payment_date: yup.string().notRequired().nullable(),
        require_external_code: yup.boolean().required(),
        external_code: yup.string().notRequired().nullable(),
        require_supplement: yup.boolean().required(),
        supplement_code: yup.string().notRequired().nullable(),
        reconciliation_only: yup.boolean().required(),
        is_draft: yup.boolean().required(),
        canceled: yup.boolean().required(),
    });

    return (
        <RhFormWrapper
            validationSchema={validationSchema}
            initialValues={initialValues}
            disabled={loading}
            onSubmit={async (formData) => {
                try {
                    await mutation({
                        variables: {
                            ExpenseDetailsInput: {
                                expense_id: expenseId,
                                notes: formData.notes || '',
                                expected_payment_date:
                                    formData.expected_payment_date || null,
                                require_external_code:
                                    formData.require_external_code,
                                external_code: formData.external_code || '',
                                require_supplement: formData.require_supplement,
                                supplement_code: formData.supplement_code || '',
                                reconciliation_only:
                                    formData.reconciliation_only,
                                is_draft: formData.is_draft,
                                canceled: formData.canceled,
                            },
                        },
                    });
                    onSuccess();
                } catch (e) {
                    console.error(e);
                }
            }}
        >
            <FormFieldSection title={'Resumen'}>
                <RhDateInput
                    name={'expected_payment_date'}
                    label={shortLabel(FIELD_LABELS.expected_payment_date)}
                />
            </FormFieldSection>
            <FormFieldSection title={'Folio del proveedor'}>
                <RhCheckbox
                    name={'require_external_code'}
                    label={shortLabel(FIELD_LABELS.require_external_code)}
                />
                <RHTextField
                    name={'external_code'}
                    label={shortLabel(FIELD_LABELS.external_code)}
                />
            </FormFieldSection>
            <FormFieldSection title={'Complemento de pago'}>
                <RhCheckbox
                    name={'require_supplement'}
                    label={shortLabel(FIELD_LABELS.require_supplement)}
                />
                <RHTextField
                    name={'supplement_code'}
                    label={shortLabel(FIELD_LABELS.supplement_code)}
                />
            </FormFieldSection>
            <FormFieldSection title={'Notas'}>
                <RHTextField
                    name={'notes'}
                    label={shortLabel(FIELD_LABELS.notes)}
                    rows={3}
                />
            </FormFieldSection>
            <FormFieldSection title={'Control del gasto'}>
                <RhCheckbox
                    name={'reconciliation_only'}
                    label={shortLabel(FIELD_LABELS.reconciliation_only)}
                />
                <RhCheckbox
                    name={'is_draft'}
                    label={shortLabel(FIELD_LABELS.is_draft)}
                />
                <RhCheckbox
                    name={'canceled'}
                    label={shortLabel(FIELD_LABELS.canceled)}
                />
            </FormFieldSection>
        </RhFormWrapper>
    );
}
